import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError, useApolloClient, useReactiveVar } from '@apollo/client';

import {
  FakturowniaInvoicesDocument,
  FakturowniaInvoicesQuery,
  FakturowniaInvoicesQueryVariables,
} from 'core/graphql';

import { IFakturowniaInvoiceListRow, IInvoicesFilters } from './types';
import { isPermissionDeniedError } from './errors';
import { financeListsVersion } from './evictFinanceLists';

interface IAllFakturowniaInvoicesHook {
  data: IFakturowniaInvoiceListRow[];
  loading: boolean;
  /** Mirrors `loading` so consumers can dim stale rows during refetch. */
  fetching: boolean;
  error?: ApolloError;
  permissionDenied: boolean;
  refetch: () => void;
}

/** Page size for each batch — `perPage` over the wire. Big enough to keep
 *  the request count low, small enough to start rendering quickly. */
const BATCH_SIZE = 200;

const EMPTY_ROWS: IFakturowniaInvoiceListRow[] = [];

/**
 * Auto-paginating list hook. Walks every page of `fakturowniaInvoices` for
 * the given filter until the backend returns a short page, then settles.
 * Rows accumulate into `data` progressively so the UI can render the first
 * batch while later pages are still in flight. Phase 9.1: replaces every
 * call site that used the page-capped `useFakturowniaInvoices` for a
 * «всё в выборке» semantic — InvoicesSummary, OverdueList, student/group
 * detail tabs etc. now get the complete set, not an arbitrary first slice.
 */
export const useAllFakturowniaInvoices = (
  filters: IInvoicesFilters,
  /**
   * `skip` — роль фактуры не читает (`FAKTUROWNIA_ACCESS_ROLES`; на карточке
   * группы — HR и методист): ни одна страница не запрашивается.
   */
  options: { skip?: boolean } = {},
): IAllFakturowniaInvoicesHook => {
  const client = useApolloClient();
  const [rows, setRows] = useState<IFakturowniaInvoiceListRow[]>([]);
  const skip = Boolean(options.skip);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<ApolloError | undefined>(undefined);
  const [refetchTick, setRefetchTick] = useState(0);
  // Explicit refetch() (sync chip, post-mutation callbacks) forces the
  // network; ordinary walks serve already-seen pages from the cache — a tab
  // revisit or the several concurrent StudentDetail instances stop
  // re-downloading the whole set (spec 0008 round 2).
  const forceNetworkRef = useRef(false);

  // Strip undefined/empty values so the variables object stays stable and
  // we don't blow the apollo cache key with `key: undefined` noise.
  const baseVariables: Omit<
    FakturowniaInvoicesQueryVariables,
    'page' | 'perPage'
  > = useMemo(
    () => ({
      direction: filters.direction || undefined,
      status: filters.status || undefined,
      matchStatus: filters.matchStatus || undefined,
      kind: filters.kind || undefined,
      studentId: filters.studentId || undefined,
      groupId: filters.groupId || undefined,
      agreementId: filters.agreementId || undefined,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      search: filters.search || undefined,
      overdueOnly: filters.overdueOnly || undefined,
      parentId: filters.parentId || undefined,
      needsLinkOnly: filters.needsLinkOnly || undefined,
      provisionBlockedOnly: filters.provisionBlockedOnly || undefined,
    }),
    [
      filters.direction,
      filters.status,
      filters.matchStatus,
      filters.kind,
      filters.studentId,
      filters.groupId,
      filters.agreementId,
      filters.dateFrom,
      filters.dateTo,
      filters.search,
      filters.overdueOnly,
      filters.parentId,
      filters.needsLinkOnly,
      filters.provisionBlockedOnly,
    ],
  );

  // Stable string key — JSON.stringify is cheap on this tiny object and
  // lets us depend on «filter values» rather than the object identity.
  const variablesKey = JSON.stringify(baseVariables);

  // Re-walk when a money mutation / sync evicts the drained field (finance-
  // block audit): the cache was just wiped, so the walk goes to the network
  // and this mounted surface repaints instead of holding a stale snapshot.
  const listsVersion = useReactiveVar(financeListsVersion);

  useEffect(() => {
    // Роль этот список не читает — не ходим вовсе (ни одной страницы).
    if (skip) {
      setLoading(false);
      setError(undefined);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    // Phase 11: stale-while-revalidate — DON'T blank `rows` here. The page
    // dims the previous list via `S.DimWhenFetching` while the next set
    // streams in, and the page-level full-screen preloader only fires when
    // `rows.length === 0` (true first mount). Wiping rows on every filter
    // change made every chip click feel like a hard navigation.

    const fetchAll = async () => {
      const accumulator: IFakturowniaInvoiceListRow[] = [];
      // Offset pagination can hand the same row on two neighbouring pages
      // when cached and fresh pages mix (or data shifts server-side mid-walk)
      // — dedup by id so sums never double-count a boundary row (audit).
      const seenIds = new Set<string>();
      let page = 1;
      try {
        // Loop until we hit a short page — that's the «no more data» signal
        // the rest of the codebase already uses (TZ §5 — pagination heuristic).
        // Sequential by design: we only know if a next page exists after
        // the current one returns. `no-await-in-loop` doesn't apply.
        // eslint-disable-next-line no-constant-condition, no-await-in-loop
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const result = await client.query<
            FakturowniaInvoicesQuery,
            FakturowniaInvoicesQueryVariables
          >({
            query: FakturowniaInvoicesDocument,
            variables: { ...baseVariables, page, perPage: BATCH_SIZE },
            fetchPolicy: forceNetworkRef.current
              ? 'network-only'
              : 'cache-first',
          });
          if (cancelled) return;

          const batch = (
            result.data?.fakturowniaInvoices || []
          ).filter((r): r is IFakturowniaInvoiceListRow => Boolean(r));
          batch.forEach((r) => {
            if (!seenIds.has(r.id)) {
              seenIds.add(r.id);
              accumulator.push(r);
            }
          });
          // Progressive update — first 200 visible while page 2/3/… load.
          setRows(accumulator.slice());

          // Termination checks the RAW page size — a deduped short page must
          // not stop the walk early.
          if (batch.length < BATCH_SIZE) break;
          page += 1;
        }
      } catch (e) {
        if (!cancelled) setError(e as ApolloError);
      } finally {
        if (!cancelled) {
          setLoading(false);
          forceNetworkRef.current = false;
        }
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
    // We depend on variablesKey (the serialized base variables), the
    // refetchTick (manual re-trigger), the eviction version and the client
    // identity. Lint can't see through JSON.stringify, so we acknowledge the
    // closure-over-baseVariables explicitly via the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variablesKey, refetchTick, listsVersion, client, skip]);

  const refetch = useCallback(() => {
    forceNetworkRef.current = true;
    setRefetchTick((t) => t + 1);
  }, []);

  return {
    // Выключенный список пуст, даже если до выключения что-то пришло.
    data: skip ? EMPTY_ROWS : rows,
    loading: skip ? false : loading,
    fetching: skip ? false : loading,
    error: skip ? undefined : error,
    permissionDenied: skip ? false : isPermissionDeniedError(error),
    refetch,
  };
};
